import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { ContactlessPayment as Nfc, CheckCircle, Truck, Printer } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { useApi, errMsg, responderUrl } from "../lib/api";
import { TAG_TYPES, COLOUR_HEX } from "../lib/constants";
import { hasWebNfc, writeAndVerify } from "../lib/nfc";
import { PageHeader, OrderStatus, AuthImg } from "../components/common";

function TagSheet({ t, onDone }) {
  const api = useApi();
  const [state, setState] = useState("");
  const url = responderUrl(t.token, "nfc");
  const write = async () => {
    setState("Hold the band to the back of the tablet…");
    try {
      const r = await writeAndVerify(url);
      if (!r.ok) throw new Error(`Verification failed – read back: ${r.readBack || "nothing"}`);
      await api.post(`/print/tags/${t.id}/written`, { verified: true, written_url: r.readBack });
      setState("Written and verified"); toast.success(`${t.bs_id} ready`); onDone();
    } catch (e) { setState(errMsg(e)); }
  };
  const markManual = async () => { await api.post(`/print/tags/${t.id}/written`, { verified: true, written_url: url }); onDone(); };
  return (
    <div className={`panel p-4 ${t.verified ? "border-emerald-600/60" : ""}`} data-testid={`sheet-${t.bs_id}`}>
      <div className="flex gap-4">
        <div className="bg-white p-2 rounded-md h-fit"><QRCodeSVG value={responderUrl(t.token, "qr")} size={96} /></div>
        <div className="flex-1 min-w-0 text-sm">
          <div className="font-mono font-semibold">{t.bs_id}</div>
          <div className="font-semibold mt-1" data-testid={`sheet-name-${t.bs_id}`}>{t.name || <span className="text-slate-500">No name printed</span>}</div>
          <div className="text-xs text-slate-400 mt-1 flex items-center gap-1">{TAG_TYPES[t.type]} · <span className="inline-block w-3 h-3 rounded-full" style={{ background: COLOUR_HEX[t.colour] }} />{t.colour}</div>
          {t.type !== "wallet_card" && <div className="mt-2 text-xs font-mono rounded bg-slate-900 border border-slate-700 px-2 py-1 inline-block" data-testid={`ice-line-${t.bs_id}`}>ICE {t.ice_blood_type || "—"} · SCAN FOR HELP</div>}
        </div>
        {t.has_photo && <AuthImg path={`/print/tags/${t.id}/photo`} className="w-16 h-16 rounded-md object-cover" />}
      </div>
      <div className="mt-3 flex flex-wrap gap-2 items-center">
        {t.verified && !t.needs_rewrite ? <span className="text-emerald-400 text-sm flex items-center gap-1" data-testid={`verified-${t.bs_id}`}><CheckCircle size={18} weight="fill" />Written & verified</span> : <>
          <Button size="sm" data-testid={`write-tag-${t.bs_id}`} disabled={!hasWebNfc()} onClick={write} className="bg-red-600 hover:bg-red-500 text-white"><Nfc size={16} className="mr-1" />Write to tag</Button>
          {!hasWebNfc() && <Button size="sm" variant="outline" data-testid={`mark-written-${t.bs_id}`} onClick={markManual}>Mark written (external writer)</Button>}</>}
        {state && <span className="text-xs text-slate-300" data-testid={`write-state-${t.bs_id}`}>{state}</span>}
      </div>
    </div>
  );
}

export default function PrintOrder() {
  const { oid } = useParams();
  const api = useApi();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get(`/print/orders/${oid}`).then((r) => setData(r.data)), [api, oid]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="text-slate-400">Loading…</div>;
  const { order, tags } = data;
  const ship = async () => { try { await api.post(`/print/orders/${oid}/ship`); toast.success("Marked shipped – tags are now active"); nav("/app/print"); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div data-testid="print-order-page">
      <PageHeader overline={`Production sheet · ${order.group_name}`} title={order.invoice_no}>
        <OrderStatus value={order.status} testid="print-order-status" />
        <Button variant="outline" data-testid="print-sheet-btn" onClick={() => window.print()}><Printer size={16} className="mr-1" />Print</Button>
        <Button data-testid="ship-order-btn" disabled={order.status === "shipped"} onClick={ship} className="bg-emerald-600 hover:bg-emerald-500 text-white"><Truck size={16} className="mr-1" />Mark shipped</Button>
      </PageHeader>
      {!hasWebNfc() && <div className="mb-6 text-sm rounded-md border border-slate-700 bg-slate-800/50 p-3 text-slate-300" data-testid="nfc-unsupported-note">Web NFC is only available in Chrome on Android. Open this page on the print tablet to write tags, or use “Mark written” if you used an external writer.</div>}
      {order.items.some((i) => !i.tag_type) && <div className="mb-6 panel p-4" data-testid="merch-items"><div className="overline mb-2">Merchandise to pack</div>
        {order.items.filter((i) => !i.tag_type).map((i, k) => <div key={k} className="text-sm">{i.quantity} × {i.name} ({i.colour})</div>)}</div>}
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">{tags.map((t) => <TagSheet key={t.id} t={t} onDone={load} />)}</div>
      <div className="print-sheet print-only"><div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, padding: 12, color: "#000" }}>
        {tags.map((t) => <div key={t.id} style={{ border: "1px solid #ccc", padding: 8, breakInside: "avoid" }}>
          <QRCodeSVG value={responderUrl(t.token, "qr")} size={110} /><div style={{ fontFamily: "monospace" }}>{t.bs_id}</div>
          <div style={{ fontWeight: 700 }}>{t.name || ""}</div><div style={{ fontSize: 11 }}>{t.type} · {t.colour} · ICE {t.ice_blood_type || "—"} SCAN FOR HELP</div></div>)}
      </div></div>
    </div>
  );
}
